"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VARIANCE_BANDS = exports.surfaceTotalsIn = exports.washLine = exports.isSprayed = exports.SPEED_PRESETS = exports.METHOD_FIELDS = exports.METHOD = void 0;
exports.effectiveSettings = effectiveSettings;
exports.lineQuantity = lineQuantity;
exports.surfaceTotals = surfaceTotals;
exports.surfaceWashArea = surfaceWashArea;
exports.areaWashArea = areaWashArea;
exports.noGirthSurfaces = noGirthSurfaces;
exports.areaHours = areaHours;
exports.areaAssessedHours = areaAssessedHours;
exports.variance = variance;
// The measuring maths — the template's (STM002 Take Off), in one place:
//   hours  = quantity × coats ÷ production rate + quantity ÷ prep rate
//   litres = quantity × coats × girth ÷ spread rate
// Girth feeds litres only (DD, 2026-10-06); a roof's pitch raises its area, so
// hours and litres (DD, 2026-10-08). The nine measuring methods are the only
// thing fixed in code; every rate and default comes from the surface type.
// Sprayed: the type's spray rate, and hours × (1 + spray uplift) (DD,
// 2026-10-13). In a repeat group: every quantity × the group's count.
// Wash m² (#510) = the m² the litres are worked from, before coats and spread:
//   quantity × girth (a roof's pitch is already in its quantity).
const surfaces_ts_1 = require("./surfaces.js");
exports.METHOD = {
    lengthWidth: 'Length × width',
    lengthHeight: 'Length × height',
    runGirth: 'Run × girth',
    door: 'Door',
    windowFrame: 'Window frame',
    stairs: 'Stairs',
    heightQuantity: 'Height × quantity',
    roof: 'Roof with pitch',
    free: 'Free (L × W × H)',
};
/** What a line's `a` and `b` are called for each method, in the template's words. */
exports.METHOD_FIELDS = {
    [exports.METHOD.lengthWidth]: { a: 'Length', b: 'Width' },
    [exports.METHOD.lengthHeight]: { a: 'Length', b: 'Height' },
    [exports.METHOD.runGirth]: { a: 'Run (m)' },
    [exports.METHOD.door]: { a: 'Width', b: 'Height', sides: true },
    [exports.METHOD.windowFrame]: { a: 'Frame width', b: 'Frame height' },
    [exports.METHOD.stairs]: { a: 'Length', b: 'Width' },
    [exports.METHOD.heightQuantity]: { a: 'Height' },
    [exports.METHOD.roof]: { a: 'Length', b: 'Width' },
    [exports.METHOD.free]: { a: 'Quantity' },
};
/** Site2Scope's speed presets for the production rate (DD, 2026-10-08). */
exports.SPEED_PRESETS = [
    { label: 'Very fast', rate: 25 },
    { label: 'Fast', rate: 20 },
    { label: 'Normal', rate: 16 },
    { label: 'Slow', rate: 12 },
    { label: 'Very slow', rate: 6 },
];
const PERCENT = 100;
/** Sprayed, and the type can be sprayed. A spray choice left on a type that lost its spray rate counts as brushed. */
const isSprayed = (surface, type) => surface.application === 'spray' && typeof type?.sprayRate === 'number' && type.sprayRate > 0;
exports.isSprayed = isSprayed;
const num = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : 0);
function effectiveSettings(surface, type) {
    const sprayed = (0, exports.isSprayed)(surface, type);
    return {
        coats: surface.coats ?? num(type?.defaultCoats),
        rate: surface.rate ?? num(sprayed ? type?.sprayRate : type?.productionRate),
        girth: surface.girth ?? (type?.girthDefault ?? 1),
        prep: surface.prep ?? num(type?.prepRate),
        spread: surface.spread ?? num(type?.spreadRate),
        uplift: sprayed ? num(type?.sprayUplift) / PERCENT : 0,
    };
}
/** One line's quantity (m² or LM), before deducting. A roof's pitch is the surface's girth value. */
function lineQuantity(method, line, girth) {
    const a = num(line.a);
    const b = num(line.b);
    const qty = num(line.qty);
    switch (method) {
        case exports.METHOD.lengthWidth:
        case exports.METHOD.lengthHeight:
        case exports.METHOD.stairs:
            return a * b * qty;
        case exports.METHOD.door:
            return a * b * (line.sides ?? 1) * qty;
        case exports.METHOD.windowFrame:
            return 2 * (a + b) * qty;
        case exports.METHOD.roof:
            return a * b * qty * girth;
        case exports.METHOD.runGirth:
        case exports.METHOD.heightQuantity:
        case exports.METHOD.free:
            return a * qty;
        default:
            return 0;
    }
}
/** Deduct lines count negative; a surface never measures below nothing. */
function surfaceQuantity(surface, girth) {
    const total = surface.lines.reduce((sum, line) => sum + (line.deduct ? -1 : 1) * lineQuantity(surface.method, line, girth), 0);
    return Math.max(0, total);
}
/** The girth litres (and the wash m²) are worked with: a roof's pitch is already in its area; every other girth scales litres only. */
const litreGirth = (surface, girth) => (surface.method === exports.METHOD.roof ? 1 : girth);
/** A surface's totals. `count` is its repeat group's count (surfaceCount): every quantity is multiplied by it. */
function surfaceTotals(surface, type, count = 1) {
    const s = effectiveSettings(surface, type);
    const quantity = surfaceQuantity(surface, s.girth) * count;
    const paintHours = s.rate > 0 ? (quantity * s.coats) / s.rate : 0;
    const prepHours = s.prep > 0 ? quantity / s.prep : 0;
    const litres = s.spread > 0 ? (quantity * s.coats * litreGirth(surface, s.girth)) / s.spread : 0;
    return { quantity, hours: (paintHours + prepHours) * (1 + s.uplift), litres };
}
/** A surface's wash m²: its quantity × the girth its litres use (an LM line's width, an item's m²). `count` as surfaceTotals. */
function surfaceWashArea(surface, type, count = 1) {
    const girth = effectiveSettings(surface, type).girth;
    return surfaceTotals(surface, type, count).quantity * Math.max(0, litreGirth(surface, girth));
}
/** The area's surfaces counted in its wash: all of them, less any an option leaves out. */
const washSurfaces = (doc, excludedIds) => doc.surfaces.filter((s) => !excludedIds.has(s.id));
/** The area's wash m² from its measured surfaces (#510), each with its group's count; before the building's count. */
function areaWashArea(doc, types, excludedIds = new Set()) {
    return washSurfaces(doc, excludedIds).reduce((sum, s) => sum + surfaceWashArea(s, types.get(s.typeId), (0, surfaces_ts_1.surfaceCount)(doc, s)), 0);
}
/** The wash's line on the proposal and the Schedule of Work: "Wash — 220 m²" (whole m², as surfaces are quoted). */
const washLine = (m2) => `Wash — ${Math.round(m2).toLocaleString('en-AU')} m²`;
exports.washLine = washLine;
/** Surfaces that are measured but add nothing to the wash m², because they have no girth: the desk shows them. */
function noGirthSurfaces(doc, types, excludedIds = new Set()) {
    return washSurfaces(doc, excludedIds)
        .filter((s) => surfaceTotals(s, types.get(s.typeId)).quantity > 0 && surfaceWashArea(s, types.get(s.typeId)) === 0)
        .map((s) => ({ id: s.id, name: (0, surfaces_ts_1.surfaceLabel)(s) }));
}
/** A surface's totals within its area's document (its group's count applied). */
const surfaceTotalsIn = (doc, surface, types) => surfaceTotals(surface, types.get(surface.typeId), (0, surfaces_ts_1.surfaceCount)(doc, surface));
exports.surfaceTotalsIn = surfaceTotalsIn;
/** The area's measured hours — stored on the area as hig_measuredhours. */
function areaHours(doc, types) {
    return doc.surfaces.reduce((sum, s) => sum + (0, exports.surfaceTotalsIn)(doc, s, types).hours, 0);
}
/** The area's assessed hours (hig_assessedhours): the PM's per-surface hours added up; null when none are assessed. */
function areaAssessedHours(doc) {
    const assessed = doc.surfaces.filter((s) => typeof s.assessedHours === 'number');
    return assessed.length ? assessed.reduce((sum, s) => sum + s.assessedHours, 0) : null;
}
/** Site2Scope's REVIEW_THRESHOLDS: within 15 % green, within 30 % amber (SCHEMA.md "Before UAT" §5). */
exports.VARIANCE_BANDS = { green: 15, amber: 30 };
/** How far assessed is from measured, both before down time: the % (signed) and its band; 'none' when either is missing. */
function variance(measured, assessed) {
    if (typeof assessed !== 'number' || !(measured > 0))
        return { percent: null, band: 'none' };
    // To one decimal, as shown — so exactly 15 % is green, not 15.000000000000002 %.
    const percent = Math.round(((assessed - measured) / measured) * PERCENT * 10) / 10;
    const size = Math.abs(percent);
    const band = size <= exports.VARIANCE_BANDS.green ? 'green' : size <= exports.VARIANCE_BANDS.amber ? 'amber' : 'red';
    return { percent, band };
}
